import { Box, Grid, Paper } from "@mui/material";
import {
	ContainmentDetails,
	ContainmentHeader,
	FormCard,
} from "components";
import { StepButtons } from "./StepButtons";
import { FileUploadPane } from "./FileUploadPane";
import { SubmitStatusScreens } from "./SubmitStatusScreens";
import { UploadStepLayout } from "./UploadStepLayout";
import { useContainmentSubmit } from "./hooks/useContainmentSubmit";
import * as hooks from "lib/hooks";
import * as routes from "enums/routes";
import {
	UploadContentTypeLPN,
	UploadContentTypeSerial,
} from "lib/util/constants";
import { ElevatedPaperStyle } from "lib/styles/shared";
import { StepButtonWrapper } from "lib/styles/button";
import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";

export function ExcelUpload(): React.JSX.Element {
	const { addSerialMode, lockMode, unlockMode, byLpn, id } = hooks.UseAddSerialMode();
	const isSingleStep = addSerialMode;
	const { f, setStepStatus, reset: resetForm } =
		hooks.UseFormContext();
	const navigate = useNavigate();
	const mccApi = hooks.UseMccApi();

	// ASRS lock/unlock: skip FlexNet PN-gate; set Upload Content Type so
	// ProcessFile applies ASRS single-column checks.
	useEffect(() => {
		if (lockMode || unlockMode) {
			f.setValue("partNumberRequired", null);
			f.setValue(
				"uploadContentType",
				byLpn ? UploadContentTypeLPN : UploadContentTypeSerial
			);
		}
	}, [lockMode, unlockMode, byLpn, f]);

	const formValues = f.getValues();
	const { status: submitStatus, error: submitError, data: submitData, submit } = useContainmentSubmit({
		mccApi,
		formValues: {
			file: formValues.file,
			containmentName: formValues.containmentName,
			description: formValues.description,
			sectorTypes: formValues.sectorTypes,
			plants: formValues.plants,
			containmentInitialHold: formValues.containmentInitialHold,
			containmentSubsequentHold: formValues.containmentSubsequentHold,
			partNumber: formValues.partNumber,
			duns: formValues.duns,
		},
		id,
		addSerialMode,
		lockMode,
		unlockMode,
		byLpn,
		setStepStatus,
	});

	const handleCancel = useCallback(() => {
		navigate(routes.Dashboard.Path);
	}, [navigate]);

	const onRouteChange = useCallback((path: string) => {
		resetForm();
		navigate(path);
	}, [resetForm, navigate]);

	const loadingTitle = lockMode
		? "Locking LPNs"
		: unlockMode
			? "Unlocking LPNs"
			: "Adding Serials";

	const successTitle = lockMode
		? "LPN Lock Request Submitted"
		: unlockMode
			? "LPN Unlock Request Submitted"
			: "Serials Added Successfully";

	if (isSingleStep && submitStatus !== "NotAsked") {
		return (
			<SubmitStatusScreens
				status={submitStatus}
				data={submitData}
				error={submitError}
				loadingTitle={loadingTitle}
				successTitle={successTitle}
				errorTitle="Submission Failed"
				onDashboard={() => onRouteChange(routes.Dashboard.Path)}
			/>
		);
	}

	const stepButtons = isSingleStep ? (
		<StepButtons
			nextHidden={f.getValues("file").IsNone()}
			nextDisabled={f.getValues("file").IsNone()}
			backDisabled
			nextText="Submit"
			onNext={submit}
			showCancel
			onCancel={handleCancel}
		/>
	) : (
		<StepButtons
			nextHidden={f.getValues("file").IsNone()}
			nextDisabled={f.getValues("file").IsNone()}
			showCancel
			onCancel={handleCancel}
		/>
	);

	if (addSerialMode) {
		// FlexNet Add Serials: warning sits at bottom of Plant Status in ContainmentDetails (SITMP-7559).
		const showFlexNetGate = !lockMode && !unlockMode;

		return (
			<Box margin="auto">
				<Grid
					container
					direction="column"
					spacing={3}
					alignItems="stretch"
					justifyContent="center"
				>
					<Grid item xs={12}>
						<ContainmentHeader />
					</Grid>
					<Grid item xs={12}>
						<UploadStepLayout
							leftPane={
								<FormCard
									label="Containment Details"
									toolTip="Details of the existing containment"
								>
									<ContainmentDetails
										id={id}
										hideDownload
										showFlexNetPartNumberGate={showFlexNetGate}
									/>
								</FormCard>
							}
							buttons={stepButtons}
						/>
					</Grid>
				</Grid>
			</Box>
		);
	}

	return (
		<Box margin="auto">
			<Grid
				container
				direction="column"
				spacing={3}
				alignItems="stretch"
				justifyContent="center"
			>
				<Grid item xs={12}>
					<ContainmentHeader />
				</Grid>
				<Grid item xs={12}>
					<Paper elevation={3} sx={ElevatedPaperStyle}>
						<FileUploadPane />
						<Box sx={StepButtonWrapper}>
							{stepButtons}
						</Box>
					</Paper>
				</Grid>
			</Grid>
		</Box>
	);
}
